'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Loader2Icon } from 'lucide-react';
import {
  tx,
  useCategoryTreeSource,
  useCurrentUser,
  useLocale,
  useBlockPart,
  type CategoryTreeNode,
} from '@akademi/site-builder-runtime';
import { Button, Card, Input, SelectField, type SelectFieldOption } from '@akademi/ui';

import { sectionInner, cardReset } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { ActivityApplicationModal } from './ActivityApplicationModal';
import { IneligibleInfo } from './IneligibleInfo';
import { AWAITING_FINAL_REGISTRATION, useFinalRegistration } from './useFinalRegistration';
import { fullVenue, shortVenue } from './venue';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * BTK-styled activity/event catalogue.
 *
 * <p>Same data path as {@code ActivityBlock} (POST {@code /api/activities},
 * proxied to the tenant event search) but the BTK page design: a filter
 * bar of dropdowns, a category pill row, a result count + sort row, and the
 * results themselves — one full-width row per event on a single list page by
 * default, or the poster-card grid when {@code layout} says so.
 *
 * <p>Filters that the backend search supports (event type, field/category,
 * city, education level) are written to the URL — the same query params the
 * {@code CategoryTree} block writes ({@code eventType} / {@code category} /
 * {@code city} / {@code educationLevel}) — so this block and a sidebar tree
 * stay in sync and a filtered view is shareable. Each card's event-type chip
 * writes the same {@code eventType} param, which holds a set, so chips from
 * several cards add up. The date range and the sort order have no counterpart
 * in the search payload, so they narrow/reorder the fetched page client-side
 * and live in component state.
 */

export interface ActivityBtkFilter {
  eventTypeIds?: Array<number | string>;
  cityIds?: Array<number | string>;
  categoryIds?: Array<number | string>;
  educationLevels?: string[];
  statuses?: string[] | null;
  searchTerm?: string;
}

export interface ActivityBlockBtkProps {
  /** Section heading. Hidden when blank. Localizable. */
  title?: string | Record<string, string>;
  /** How many cards to fetch per page. Default 9. */
  maxShow?: number;
  /** Pagination offset forwarded to the API. Default 0. */
  firstResult?: number;
  /**
   * How results are laid out. {@code 'list'} (default) renders every event as
   * one full-width row on a single list page — the BTK requirement that
   * replaced the paged 3-up grid; {@code 'grid'} keeps the poster-card grid.
   */
  layout?: 'grid' | 'list';
  /** Grid columns on desktop (2/3/4 — collapses to 2 then 1). Default 3.
   *  Ignored in list layout. */
  columns?: 2 | 3 | 4;
  /** Which result bucket to render. Default {@code upcoming}. */
  bucket?: 'upcoming' | 'missed' | 'previous' | 'all';
  /** Render the dropdown filter bar. Default true. */
  showFilters?: boolean;
  /** Render the category pill row. Default true. */
  showCategoryPills?: boolean;
  /** How many pills to show before the "…" expander. Default 8. */
  pillLimit?: number;
  /** Render the sort dropdown. Default true. */
  showSort?: boolean;
  /** Render the "{n} etkinlik bulundu" line. Default true. */
  showCount?: boolean;
  /** Render a search input beside the sort dropdown. Pressing Enter (or
   *  clearing the field) writes {@code ?term=…} and re-fetches. Default false. */
  showSearch?: boolean;
  /** Placeholder text for the search input. Localizable. */
  searchPlaceholder?: string | Record<string, string>;
  /** Days before the deadline at which the card flips to the urgency
   *  badge ("Son 3 Gün"). Default 3. */
  urgentDays?: number;
  /** Message shown when nothing matches. Localizable. */
  emptyText?: string | Record<string, string>;
  /** Label for the load-more action. Localizable. */
  showMoreText?: string | Record<string, string>;
  /** Suffix after the result count (e.g. "etkinlik bulundu."). Localizable. */
  resultSuffix?: string | Record<string, string>;
  /** Label for the detail button. Localizable. */
  detailLabel?: string | Record<string, string>;
  /** Label for the apply button. Localizable. */
  applyLabel?: string | Record<string, string>;
  /** Label for the already-applied state. Localizable. */
  pendingLabel?: string | Record<string, string>;
  /** Prefix before the application deadline. Localizable. */
  lastEntryLabel?: string | Record<string, string>;
  /** Suffix after the duration number (e.g. "Saat"). Localizable. */
  hourLabel?: string | Record<string, string>;
  /** Label of the "clear all filters" button. Localizable. */
  clearFiltersLabel?: string | Record<string, string>;
  /**
   * Auth-gate the whole block. 'any' (default) always renders;
   * 'in' only for signed-in users; 'out' only for anonymous visitors.
   */
  requireAuth?: 'any' | 'in' | 'out';
  /** Operator-set base filter, merged with the URL params. */
  filter?: ActivityBtkFilter;
  sortField?: string;
  sortOrder?: 'ASCENDING' | 'DESCENDING';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** Normalised card shape returned by {@code /api/activities}. */
interface Activity {
  id: number | string;
  title?: string;
  description?: string;
  startDate?: number;
  endDate?: number;
  lastEntryDate?: number;
  url?: string;
  city?: string;
  /** Venue detail beyond the city (9465). */
  district?: string;
  address?: string;
  maxEntries?: number;
  duration?: number;
  courseCode?: string;
  status?: string;
  educationLevels?: string[];
  thumbnail?: string;
  entriesClosed?: boolean;
  quotaFull?: boolean;
  registered?: boolean;
  /**
   * 9476 SHOW_BLOCKED — false means the tenant screens eligibility and this
   * viewer fails it. Undefined is "not screened", never "ineligible".
   */
  eligible?: boolean;
  /** Backend-localised reason behind `eligible: false`. */
  ineligibleReason?: string;
  /** Every reason behind `eligible: false` — what the info badge lists. */
  ineligibleReasons?: string[];
  /** Dynamic application form id (9464) — forwarded to the apply modal. */
  applicationFormId?: number;
  /** Event type name (e.g. "Yüz Yüze", "Çevrim İçi"). */
  eventType?: string;
  /** First category / field name (e.g. "Yapay Zekâ"). */
  category?: string;
}

interface ActivitiesResponse {
  upcoming?: Activity[];
  missed?: Activity[];
  previous?: Activity[];
  totals?: { upcoming: number; missed: number; previous: number };
}

type DateRange = 'all' | 'week' | 'month' | 'quarter';
type SortKey = 'upcoming' | 'deadline' | 'newest' | 'titleAsc' | 'titleDesc';

interface CardLabels {
  detail: string;
  apply: string;
  pending: string;
  lastEntry: string;
  hour: string;
  open: string;
  closed: string;
  quotaFull: string;
  registered: string;
  ineligible: string;
  /** 9476 — heading of the popover that spells out WHY the viewer is blocked. */
  ineligibleWhy: string;
  /** 9476 — accessible name / tooltip of the info badge that opens it. */
  ineligibleWhyHint: string;
  /** Kesin kayıt (9482) — shown only to a participant whose entry is AWAITING. */
  confirmFinal: string;
  cancelFinal: string;
  finalConfirmed: string;
  finalCancelled: string;
  finalQuotaFull: string;
  lastDay: string;
  /** Tooltip on the clickable event-type chip. */
  filterByType: string;
  /** `(n) => "Son 3 Gün"` */
  daysLeft: (n: number) => string;
}

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');

const DAY_MS = 86_400_000;

/** URL params this block owns — cleared together by "Filtreleri Temizle". */
const FILTER_PARAMS = ['eventType', 'category', 'city', 'educationLevel', 'term'] as const;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function parseCommaParam(params: URLSearchParams, key: string): string[] {
  return (
    params
      .get(key)
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean) ?? []
  );
}

function mergeUniq(...arrays: (string[] | undefined)[]): string[] {
  return [...new Set(arrays.flatMap((a) => a ?? []))];
}

/** Key for matching a card's type/category NAME against a taxonomy option. */
function normaliseName(name: string): string {
  return name.trim().toLocaleLowerCase('tr');
}

async function fetchActivities(
  body: Record<string, unknown>,
  signal: AbortSignal,
): Promise<ActivitiesResponse | null> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/api/activities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  return (await res.json()) as ActivitiesResponse;
}

function pickBucket(data: ActivitiesResponse, bucket: ActivityBlockBtkProps['bucket']): Activity[] {
  const up = data.upcoming ?? [];
  const missed = data.missed ?? [];
  const prev = data.previous ?? [];
  switch (bucket) {
    case 'missed':
      return missed;
    case 'previous':
      return prev;
    case 'all':
      return [...up, ...missed, ...prev];
    case 'upcoming':
    default:
      return up;
  }
}

/**
 * How many rows exist for {@code bucket} — drives whether "load more" shows.
 *
 * <p>A page that came back SHORT ({@code pageCount < max}) is the end of the
 * feed, full stop: clamp to {@code loaded} so the button hides. The backend's
 * {@code totals.totalRecords} is only trusted while pages keep coming back
 * full — it has been seen to over-count (claims 25, stops serving rows at 18),
 * which left the button up forever with every click fetching nothing.
 */
function bucketTotal(
  data: ActivitiesResponse,
  bucket: ActivityBlockBtkProps['bucket'],
  loaded: number,
  pageCount: number,
  max: number,
): number {
  if (pageCount < max) return loaded;
  const t = data.totals;
  if (t) {
    if (bucket === 'all') return (t.upcoming ?? 0) + (t.missed ?? 0) + (t.previous ?? 0);
    return t[(bucket ?? 'upcoming') as 'upcoming' | 'missed' | 'previous'] ?? 0;
  }
  // No totals in the response: a full page hints there may be more.
  return loaded + 1;
}

/** Flatten a (possibly nested) category tree into a single option list. */
function flattenTree(nodes: CategoryTreeNode[], depth = 0): Array<{ id: string; name: string }> {
  const out: Array<{ id: string; name: string }> = [];
  for (const node of nodes) {
    out.push({ id: String(node.id), name: node.name });
    if (depth < 2 && Array.isArray(node.children) && node.children.length > 0) {
      out.push(...flattenTree(node.children, depth + 1));
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Block
// ---------------------------------------------------------------------------

export function ActivityBlockBtk({
  title,
  maxShow,
  firstResult,
  layout = 'list',
  columns = 3,
  bucket = 'upcoming',
  showFilters = true,
  showCategoryPills = true,
  pillLimit = 8,
  showSort = true,
  showCount = true,
  showSearch = true,
  searchPlaceholder,
  urgentDays = 3,
  emptyText,
  showMoreText,
  resultSuffix,
  detailLabel,
  applyLabel,
  pendingLabel,
  lastEntryLabel,
  hourLabel,
  clearFiltersLabel,
  requireAuth,
  filter,
  sortField,
  sortOrder,
  paddingOuter,
  paddingInner,
}: ActivityBlockBtkProps) {
  const { locale } = useLocale();
  const { t } = useTranslation('common');
  const part = useBlockPart();
  const currentUser = useCurrentUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const treeSource = useCategoryTreeSource();

  /** Translation with an operator override and a hard-coded last resort.
   *  i18next returns the key itself when a key is missing, so compare
   *  against it before trusting the result. */
  function label(key: string, prop: string | Record<string, string> | undefined, fallback: string) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
    return tx(prop) || fallback;
  }

  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);

  const max = clamp(typeof maxShow === 'number' ? maxShow : 9, 1, 50);
  const first = clamp(typeof firstResult === 'number' ? firstResult : 0, 0, 10_000);
  const sort = (sortField ?? '').trim() || 'recent';
  const order = sortOrder === 'DESCENDING' ? 'DESCENDING' : 'ASCENDING';
  // Stored props may carry the column count as a string ("4") — older admin
  // builds saved numeric enum picks straight off the `<select>` — so coerce
  // before clamping instead of matching the number literals strictly.
  const cols = clamp(Math.trunc(Number(columns)) || 3, 2, 4);

  // ── Client-only narrowing (no backend counterpart) ───────────────────────
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [sortKey, setSortKey] = useState<SortKey>('upcoming');
  const [pillsExpanded, setPillsExpanded] = useState(false);

  // ── Server-side filters, mirrored in the URL ─────────────────────────────
  const mergedFilter = {
    eventTypeIds: mergeUniq(
      filter?.eventTypeIds?.map(String),
      parseCommaParam(searchParams, 'eventType'),
    ),
    cityIds: mergeUniq(filter?.cityIds?.map(String), parseCommaParam(searchParams, 'city')),
    categoryIds: mergeUniq(
      filter?.categoryIds?.map(String),
      parseCommaParam(searchParams, 'category'),
    ),
    educationLevels: mergeUniq(
      filter?.educationLevels,
      parseCommaParam(searchParams, 'educationLevel'),
    ),
    statuses: filter?.statuses ?? null,
    searchTerm: searchParams.get('term')?.trim() || filter?.searchTerm || '',
  };

  const baseBody = {
    maxResults: max,
    sortField: sort,
    sortOrder: order,
    filter: mergedFilter,
  };
  const bodyKey = JSON.stringify({ ...baseBody, first, bucket });

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set(key, value);
    else params.delete(key);
    const qs = params.toString();
    router.push(qs ? `?${qs}` : '?', { scroll: false });
  }

  // ── Search input (local state mirrors ?term= on mount) ───────────────────
  const [searchInput, setSearchInput] = useState(searchParams.get('term') ?? '');

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') setParam('term', '');
  }

  function handleSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') setParam('term', searchInput.trim());
  }

  /** Ids currently in `?eventType=` — the param is a comma-separated set. */
  const selectedEventTypes = parseCommaParam(searchParams, 'eventType');

  /**
   * Toggle one event type from a card chip (9493).
   *
   * <p>Cards carry the type NAME while the URL param carries taxonomy ids (the
   * filter dropdown writes those, and the search payload wants them), so the
   * chip resolves its id through the loaded `activity_type` options. A name
   * with no match — a type removed from the taxonomy but still on an event —
   * isn't clickable at all, so this can only be reached with a resolvable one.
   */
  function toggleEventType(name: string) {
    const id = eventTypeIdByName.get(normaliseName(name));
    if (!id) return;
    const next = selectedEventTypes.includes(id)
      ? selectedEventTypes.filter((v) => v !== id)
      : [...selectedEventTypes, id];
    setParam('eventType', next.join(','));
  }

  function clearFilters() {
    const params = new URLSearchParams(searchParams.toString());
    for (const key of FILTER_PARAMS) params.delete(key);
    setDateRange('all');
    setSearchInput('');
    const qs = params.toString();
    router.push(qs ? `?${qs}` : '?', { scroll: false });
  }

  // ── Feed state ───────────────────────────────────────────────────────────
  const [items, setItems] = useState<Activity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!hidden);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (hidden) {
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setLoadError(false);

    void fetchActivities({ firstResult: first, ...baseBody }, ctrl.signal).then((data) => {
      if (ctrl.signal.aborted) return;
      if (!data) {
        setItems([]);
        setTotal(0);
        setLoadError(true);
        setLoading(false);
        return;
      }
      const raw = pickBucket(data, bucket).slice(0, max);
      setItems(raw);
      setTotal(bucketTotal(data, bucket, raw.length, raw.length, max));
      setLoading(false);
    });

    return () => ctrl.abort();
    // `bodyKey` is the serialised request body — it changes whenever any
    // part of the query (filters, paging, sort) does.
  }, [bodyKey, hidden]);

  async function handleLoadMore() {
    if (loadingMore) return;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoadingMore(true);
    try {
      const data = await fetchActivities({ firstResult: items.length, ...baseBody }, ctrl.signal);
      if (ctrl.signal.aborted) return;
      if (data) {
        const raw = pickBucket(data, bucket).slice(0, max);
        if (raw.length > 0) setItems((prev) => [...prev, ...raw]);
        setTotal(bucketTotal(data, bucket, items.length + raw.length, raw.length, max));
      }
    } finally {
      if (!ctrl.signal.aborted) setLoadingMore(false);
    }
  }

  // ── Filter option feeds ──────────────────────────────────────────────────
  // Event types load even with the filter bar hidden: the cards' type chips
  // are clickable filters (9493) and need the name → id map to write the URL.
  const eventTypes = useCategoryOptions(treeSource, 'activity_type', true);
  const eventTypeIdByName = useMemo(
    () => new Map(eventTypes.map((o) => [normaliseName(o.name), o.id])),
    [eventTypes],
  );
  const cities = useCategoryOptions(treeSource, 'activity_provinces', showFilters);
  const educations = useCategoryOptions(treeSource, 'activity_educational', showFilters);
  const categories = useCategoryOptions(
    treeSource,
    'activity_categories',
    showFilters || showCategoryPills,
  );

  // ── Copy ─────────────────────────────────────────────────────────────────
  const heading = tx(title);
  const allLabel = label('activity.all', undefined, 'Tümü');
  const emptyMsg = label('activity.empty', emptyText, 'Gösterilecek etkinlik yok.');
  const moreText = label('button.showMore', showMoreText, 'Daha Fazla Göster');
  const countLabel = label('activity.activitiesFound', resultSuffix, 'etkinlik bulundu');
  const clearLabel = label('activity.clearFilters', clearFiltersLabel, 'Filtreleri Temizle');
  const sortPrefix = label('activity.sortBy', undefined, 'Sırala');
  const searchTermPlaceholder = label(
    'activity.searchPlaceholder',
    searchPlaceholder,
    'Etkinlik adı, kod veya il ile ara',
  );
  /** Copy for the dropdowns' in-panel filter box (SelectField shows it on its
   *  own once a list runs past ten options). */
  const searchCopy = {
    searchPlaceholder: label('search.search', undefined, 'Ara…'),
    noResultsText: label('search.noResults', undefined, 'Sonuç bulunamadı'),
  };

  const cardLabels: CardLabels = {
    detail: label('activity.viewDetails', detailLabel, 'Detayları Gör'),
    apply: label('activity.apply', applyLabel, 'Başvur'),
    pending: label('activity.pendingApproval', pendingLabel, 'Onay Bekliyor'),
    // Short form — the poster card is narrow, so it uses its own key and
    // only falls back to the long `applicationDeadline` copy.
    lastEntry: label('activity.deadlineShort', lastEntryLabel, 'Son Başvuru:'),
    hour: label('activity.hours', hourLabel, 'Saat'),
    open: label('activity.applicationsOpen', undefined, 'Başvurular Açık'),
    closed: label('activity.applicationsClosed', undefined, 'Başvurular Kapalı'),
    quotaFull: label('activity.quotaFull', undefined, 'Kontenjan Dolu'),
    registered: label('activity.registered', undefined, 'Kayıtlı'),
    ineligible: label('activity.ineligible', undefined, 'Şartları Karşılamıyorsunuz'),
    ineligibleWhy: label('activity.ineligibleReasonTitle', undefined, 'Neden başvuramıyorum?'),
    ineligibleWhyHint: label('activity.ineligibleReasonHint', undefined, 'Nedenini gör'),
    confirmFinal: label('activity.confirmFinal', undefined, 'Kesin Kaydımı Yap'),
    cancelFinal: label('activity.cancelFinal', undefined, 'Kaydımı İptal Et'),
    finalConfirmed: label('activity.finalConfirmed', undefined, 'Kesin Kaydınız Alındı'),
    finalCancelled: label('activity.finalCancelled', undefined, 'Kaydınız İptal Edildi'),
    finalQuotaFull: label('activity.finalQuotaFull', undefined, 'Kontenjan Doldu'),
    lastDay: label('activity.lastDay', undefined, 'Son Gün'),
    filterByType: label('activity.filterByType', undefined, 'Bu türe göre filtrele'),
    daysLeft: (n: number) => {
      const translated = t('activity.daysLeft', { count: n });
      return translated && translated !== 'activity.daysLeft' ? translated : `Son ${n} Gün`;
    },
  };

  const dateOptions: SelectFieldOption[] = [
    { value: 'all', label: label('activity.allDates', undefined, 'Tüm Tarihler') },
    { value: 'week', label: label('activity.thisWeek', undefined, 'Bu Hafta') },
    { value: 'month', label: label('activity.thisMonth', undefined, 'Bu Ay') },
    { value: 'quarter', label: label('activity.nextThreeMonths', undefined, 'Önümüzdeki 3 Ay') },
  ];

  const sortOptions: SelectFieldOption[] = [
    { value: 'upcoming', label: label('activity.sortUpcoming', undefined, 'Yaklaşan Etkinlikler') },
    { value: 'deadline', label: label('activity.sortDeadline', undefined, 'Son Başvuru Tarihi') },
    { value: 'newest', label: label('activity.sortNewest', undefined, 'Yeni Eklenenler') },
    { value: 'titleAsc', label: label('activity.sortTitleAsc', undefined, 'İsme Göre (A–Z)') },
    { value: 'titleDesc', label: label('activity.sortTitleDesc', undefined, 'İsme Göre (Z–A)') },
  ];

  // ── Client-side narrowing + ordering ─────────────────────────────────────
  /**
   * "Load More" only ever appends to `items` (see {@link handleLoadMore}),
   * but a plain {@code narrowAndSort(items, …)} resorts the WHOLE list on
   * every change. The client sort key has no relationship to the backend
   * page order, so a freshly-fetched page can sort into the middle of what's
   * already on screen — cards the user is already looking at shift around,
   * and the new page reads as being inserted above the fold instead of
   * appended below it.
   *
   * <p>So a pure append — same narrowing/ordering criteria, `items` grew and
   * its existing prefix is untouched — only sorts+filters the new tail and
   * appends it after the already-computed view. Anything else (a fresh fetch
   * replacing `items` wholesale, or the user changing the date/sort
   * dropdown) recomputes from scratch, which is exactly when a full resort
   * is the correct, expected behaviour.
   */
  // Append detection needs the PREVIOUS committed {items, visible}. The ref
  // that carries it is written in an effect, NEVER during render: a
  // render-time write desyncs from the committed tree whenever React throws a
  // render away (StrictMode's double-invoke, a concurrent re-render, a
  // bail-out), and then the next real "Load more" is misread as a
  // criteria change — `narrowAndSort` re-sorts the WHOLE list, the cards the
  // user is looking at jump, and the fresh page reads as inserted at the top
  // instead of appended at the bottom.
  const visibleRef = useRef<{ items: Activity[]; critKey: string; visible: Activity[] }>({
    items: [],
    critKey: '',
    visible: [],
  });
  const critKey = `${dateRange}|${sortKey}|${locale}`;

  const visible = useMemo(() => {
    const prev = visibleRef.current;
    const isAppend =
      prev.critKey === critKey &&
      prev.items.length > 0 &&
      items.length > prev.items.length &&
      prev.items.every((it, i) => it === items[i]);
    return isAppend
      ? [
          ...prev.visible,
          ...narrowAndSort(items.slice(prev.items.length), dateRange, sortKey, locale),
        ]
      : narrowAndSort(items, dateRange, sortKey, locale);
  }, [items, critKey, dateRange, sortKey, locale]);

  useEffect(() => {
    visibleRef.current = { items, critKey, visible };
  }, [items, critKey, visible]);

  if (hidden) return null;

  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);
  const selectedCategory = searchParams.get('category') ?? '';
  const hasActiveFilters =
    selectedEventTypes.length > 0 ||
    Boolean(selectedCategory) ||
    Boolean(searchParams.get('city')) ||
    Boolean(searchParams.get('educationLevel')) ||
    Boolean(searchParams.get('term')) ||
    dateRange !== 'all';
  // Never offer "load more" with nothing loaded (an empty first page, or a
  // filtered-to-zero view) — there is nothing to page past.
  const hasMore = items.length > 0 && total > items.length && dateRange === 'all';
  const isList = layout !== 'grid';
  const resultsStyle: CSSProperties = isList
    ? listStyle
    : {
        display: 'grid',
        gap: 20,
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      };

  /** Chip behaviour for one card's event type — omitted when the name has no
   *  taxonomy match, which is what makes {@link EventTypeBadge} fall back to
   *  plain text. */
  function typeChipFor(item: Activity): TypeChipAction | undefined {
    const name = item.eventType?.trim();
    if (!name) return undefined;
    const id = eventTypeIdByName.get(normaliseName(name));
    if (!id) return undefined;
    return { active: selectedEventTypes.includes(id), onToggle: () => toggleEventType(name) };
  }

  const visiblePills = pillsExpanded ? categories : categories.slice(0, clamp(pillLimit, 1, 30));
  const hasHiddenPills = categories.length > visiblePills.length;

  return (
    <BlockRoot as="div" style={outerPad}>
      <style>{BTK_CSS}</style>
      <div style={{ ...sectionInner, ...innerPad }}>
        {heading ? (
          <h2 style={headingStyle} {...part('title')}>
            {heading}
          </h2>
        ) : null}

        {showFilters ? (
          <div className="akd-btk-filters" style={filterBarStyle} {...part('filter', 0)}>
            <FilterSelect
              icon={<IconTicket />}
              caption={label('activity.eventTypeFilter', undefined, 'Etkinlik Türü')}
              // The param holds a set (card chips can add several); a
              // single-select trigger can only stand for one of them, so it
              // shows the value while exactly one is picked and falls back to
              // its caption once the selection is wider than the control.
              value={selectedEventTypes.length === 1 ? selectedEventTypes[0]! : ''}
              options={withAll(eventTypes, allLabel)}
              onChange={(v) => setParam('eventType', v)}
              {...searchCopy}
            />
            <FilterSelect
              icon={<IconGrid />}
              caption={label('activity.fieldFilter', undefined, 'Alan')}
              value={selectedCategory}
              options={withAll(categories, allLabel)}
              onChange={(v) => setParam('category', v)}
              {...searchCopy}
            />
            <FilterSelect
              icon={<IconMapPin />}
              caption={label('activity.cityFilter', undefined, 'Şehir')}
              value={searchParams.get('city') ?? ''}
              options={withAll(cities, allLabel)}
              onChange={(v) => setParam('city', v)}
              {...searchCopy}
            />
            <FilterSelect
              icon={<IconCalendar />}
              caption={label('activity.dateFilter', undefined, 'Tarih')}
              value={dateRange}
              emptyValue="all"
              options={dateOptions}
              onChange={(v) => setDateRange(v as DateRange)}
              {...searchCopy}
            />
            <FilterSelect
              icon={<IconGraduation />}
              caption={label('activity.educationFilter', undefined, 'Öğrenim Durumu')}
              value={searchParams.get('educationLevel') ?? ''}
              options={withAll(educations, allLabel)}
              onChange={(v) => setParam('educationLevel', v)}
              {...searchCopy}
            />
          </div>
        ) : null}

        {showCategoryPills && categories.length > 0 ? (
          <div className="akd-btk-pills" style={pillRowStyle} {...part('filter', 1)}>
            <Pill active={!selectedCategory} onClick={() => setParam('category', '')}>
              {allLabel}
            </Pill>
            {visiblePills.map((c) => (
              <Pill
                key={c.id}
                active={selectedCategory === c.id}
                onClick={() => setParam('category', selectedCategory === c.id ? '' : c.id)}
              >
                {c.name}
              </Pill>
            ))}
            {hasHiddenPills || pillsExpanded ? (
              <Pill
                active={false}
                onClick={() => setPillsExpanded((v) => !v)}
                aria-label={pillsExpanded ? allLabel : '…'}
              >
                {pillsExpanded ? '×' : '···'}
              </Pill>
            ) : null}
          </div>
        ) : null}

        {showCount || showSort || showSearch || hasActiveFilters ? (
          <div style={resultRowStyle}>
            <div className="akd-btk-result-left" style={resultLeftStyle}>
              {hasActiveFilters ? (
                <Button
                  onClick={clearFilters}
                  className="akd-btk-clear"
                  variant="brand-outlined"
                  {...part('action')}
                >
                  <IconRefresh />
                  {clearLabel}
                </Button>
              ) : null}
              {showCount ? (
                <span className="flex gap-1 items-center" style={countStyle}>
                  {loading ? <Loading /> : visible.length === items.length ? total : visible.length}{' '}
                  {countLabel}
                </span>
              ) : null}
            </div>
            {showSort || showSearch ? (
              <div className="akd-btk-sort-wrap" style={sortWrapStyle}>
                {showSearch ? (
                  <Input
                    type="search"
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKey}
                    placeholder={searchTermPlaceholder}
                    className="akd-btk-search"
                    style={searchInputStyle}
                    {...part('search')}
                  />
                ) : null}
                {showSort ? (
                  <SelectField
                    value={sortKey}
                    options={sortOptions}
                    onChange={(v) => setSortKey(v as SortKey)}
                    ariaLabel={sortPrefix}
                    placement="bottom-end"
                    className="akd-btk-sort-select"
                    style={sortSelectStyle}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <div style={resultsStyle}>
            {Array.from({ length: isList ? Math.min(max, 4) : Math.min(max, cols) }).map((_, i) => (
              <div key={i} style={isList ? rowSkeletonStyle : skeletonStyle} />
            ))}
          </div>
        ) : loadError ? (
          <p style={messageStyle}>
            {label('activity.loadError', undefined, 'Etkinlikler yüklenemedi.')}
          </p>
        ) : visible.length === 0 ? (
          <p style={messageStyle}>{emptyMsg}</p>
        ) : (
          <div
            className={isList ? 'akd-btk-list' : 'akd-btk-grid'}
            style={resultsStyle}
            {...part('list')}
          >
            {visible.map((item) => (
              <ActivityItem
                key={item.id}
                item={item}
                variant={isList ? 'row' : 'card'}
                locale={locale}
                labels={cardLabels}
                urgentDays={urgentDays}
                isSignedIn={isSignedIn}
                typeChip={typeChipFor(item)}
              />
            ))}
          </div>
        )}

        {hasMore && !loading && !loadError ? (
          <div style={showMoreWrapStyle}>
            <Button
              onClick={() => void handleLoadMore()}
              disabled={loadingMore}
              variant="brand-outlined"
              size="lg"
              {...part('pager')}
            >
              {loadingMore ? <Loading /> : moreText}
            </Button>
          </div>
        ) : null}
      </div>
    </BlockRoot>
  );
}

// ---------------------------------------------------------------------------
// Filter option feed
// ---------------------------------------------------------------------------

/** Fetch one activity taxonomy through the host CategoryTree source.
 *  Returns {@code []} until it resolves (or when {@code enabled} is false). */
function useCategoryOptions(
  source: ReturnType<typeof useCategoryTreeSource>,
  type: string,
  enabled: boolean,
): Array<{ id: string; name: string }> {
  const [options, setOptions] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (!source || !enabled) {
      setOptions([]);
      return;
    }
    const ctrl = new AbortController();
    source
      .getTree(type, { signal: ctrl.signal })
      .then((tree) => {
        if (ctrl.signal.aborted) return;
        setOptions(flattenTree(tree));
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setOptions([]);
      });
    return () => ctrl.abort();
  }, [source, type, enabled]);

  return options;
}

function withAll(
  options: Array<{ id: string; name: string }>,
  allLabel: string,
): SelectFieldOption[] {
  return [{ value: '', label: allLabel }, ...options.map((o) => ({ value: o.id, label: o.name }))];
}

// ---------------------------------------------------------------------------
// Client-side narrowing / ordering
// ---------------------------------------------------------------------------

function rangeEnd(range: DateRange): number | null {
  const now = Date.now();
  switch (range) {
    case 'week':
      return now + 7 * DAY_MS;
    case 'month':
      return now + 30 * DAY_MS;
    case 'quarter':
      return now + 90 * DAY_MS;
    default:
      return null;
  }
}

/**
 * Title comparator for the A–Z / Z–A order (9497).
 *
 * <p>`numeric: true` is what makes one control cover both halves of the
 * requirement ("harfe ve sayıya göre"): titles that lead with a number order
 * 2 → 10 → 11 instead of the lexicographic 10 → 11 → 2, while everything else
 * collates alphabetically under the viewer's locale (İ/ı, Ö, Ş … in Turkish).
 */
function titleCollator(locale: string): Intl.Collator {
  return new Intl.Collator(locale || 'tr', { numeric: true, sensitivity: 'base' });
}

function narrowAndSort(
  items: Activity[],
  range: DateRange,
  sortKey: SortKey,
  locale: string,
): Activity[] {
  const until = rangeEnd(range);
  const filtered =
    until == null
      ? items
      : items.filter((i) => typeof i.startDate === 'number' && i.startDate <= until);

  const sorted = [...filtered];

  if (sortKey === 'titleAsc' || sortKey === 'titleDesc') {
    const collator = titleCollator(locale);
    const direction = sortKey === 'titleAsc' ? 1 : -1;
    sorted.sort((a, b) => direction * collator.compare(a.title ?? '', b.title ?? ''));
    return sorted;
  }

  const key: keyof Activity = sortKey === 'deadline' ? 'lastEntryDate' : 'startDate';
  sorted.sort((a, b) => {
    const av = typeof a[key] === 'number' ? (a[key] as number) : Number.MAX_SAFE_INTEGER;
    const bv = typeof b[key] === 'number' ? (b[key] as number) : Number.MAX_SAFE_INTEGER;
    return sortKey === 'newest' ? bv - av : av - bv;
  });
  return sorted;
}

// ---------------------------------------------------------------------------
// Filter bar control — icon + caption stacked over the select trigger
// ---------------------------------------------------------------------------

/** One filter-bar field. The SelectField trigger IS the whole box (icon and
 *  caption live inside it), so clicking anywhere in the field opens the
 *  dropdown and the panel lines up with the field's full width. */
function FilterSelect({
  icon,
  caption,
  value,
  emptyValue = '',
  options,
  onChange,
  searchPlaceholder,
  noResultsText,
}: {
  icon: ReactNode;
  caption: string;
  value: string;
  /** The "nothing narrowed down yet" value. While the field sits on it the
   *  trigger shows the caption alone — no "Tümü" second line. */
  emptyValue?: string;
  options: SelectFieldOption[];
  onChange: (value: string) => void;
  searchPlaceholder: string;
  noResultsText: string;
}): ReactNode {
  // Radix's Select.Item rejects an empty-string value (reserved for "no
  // selection"), but `withAll()` builds its "Tümü" option with value ''
  // (matching this component's own default `emptyValue`) — remap just
  // that one option to a sentinel so it can render, translating back to
  // '' in onChange. Invisible to callers: when `value` is the empty state
  // the trigger already collapses to caption-only (below), so there's no
  // "selected item" text depending on the remapped value matching.
  const emptyOptionSentinel = '__filter_all__';
  const safeOptions =
    emptyValue === ''
      ? options.map((o) => (o.value === '' ? { ...o, value: emptyOptionSentinel } : o))
      : options;
  return (
    <SelectField
      value={value}
      options={safeOptions}
      onChange={(v) => onChange(v === emptyOptionSentinel ? '' : v)}
      ariaLabel={caption}
      icon={icon}
      caption={caption}
      captionOnly={value === emptyValue}
      panelWidth="trigger"
      fullWidth
      // Long lists (cities, fields) get an in-panel filter box automatically.
      searchPlaceholder={searchPlaceholder}
      noResultsText={noResultsText}
      style={filterFieldStyle}
    />
  );
}

function Pill({
  active,
  onClick,
  children,
  ...rest
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="akd-btk-pill"
      style={active ? pillActiveStyle : pillStyle}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

const EDU_LABELS: Record<string, string> = {
  PRIMARY_SCHOOL: 'İlköğretim',
  MIDDLE_SCHOOL: 'Ortaöğretim',
  HIGH_SCHOOL: 'Lise',
  ASSOCIATE_DEGREE: 'Ön Lisans',
  BACHELORS_DEGREE: 'Lisans',
  MS_MBA: 'Yüksek Lisans',
  DOCTORAL: 'Doktora',
};

type StatusTone = 'open' | 'urgent' | 'closed' | 'registered';

function statusOf(
  item: Activity,
  labels: CardLabels,
  urgentDays: number,
): { tone: StatusTone; text: string } {
  if (item.registered) return { tone: 'registered', text: labels.registered };
  if (item.quotaFull) return { tone: 'closed', text: labels.quotaFull };
  if (item.entriesClosed) return { tone: 'closed', text: labels.closed };
  // 9476 — an explicit false only; undefined means the tenant does not screen.
  // Deliberately AFTER the closed checks: an event nobody can apply to is
  // closed, not "you don't qualify", and the latter reads as a personal
  // rejection for what is really a full or finished event.
  if (item.eligible === false) return { tone: 'closed', text: labels.ineligible };
  if (typeof item.lastEntryDate === 'number') {
    const left = Math.ceil((item.lastEntryDate - Date.now()) / DAY_MS);
    if (left <= 0) return { tone: 'urgent', text: labels.lastDay };
    if (left <= urgentDays) return { tone: 'urgent', text: labels.daysLeft(left) };
  }
  return { tone: 'open', text: labels.open };
}

/** What a clickable event-type chip needs from the block (9493). Absent when
 *  the type has no match in the `activity_type` taxonomy — nothing to filter
 *  by, so the chip stays plain text. */
interface TypeChipAction {
  active: boolean;
  onToggle: () => void;
}

/**
 * One event, as either a poster card (grid layout) or a full-width row (list
 * layout). Both variants show the same data and share the apply flow — only
 * the arrangement differs — so they live in one component rather than two that
 * drift apart.
 */
function ActivityItem({
  item,
  variant,
  locale,
  labels,
  urgentDays,
  isSignedIn,
  typeChip,
}: {
  item: Activity;
  variant: 'card' | 'row';
  locale: string;
  labels: CardLabels;
  urgentDays: number;
  isSignedIn: boolean;
  typeChip?: TypeChipAction;
}): ReactNode {
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);

  /**
   * Kesin kayıt straight from the card (9482). The entry status rides on the
   * activity, so the invitation is visible to the participant who is already on
   * the site instead of only in the mail they may have lost.
   */
  const finalReg = useFinalRegistration(item.id);
  const entryStatus = finalReg.status ?? item.status;
  const awaitingFinal = entryStatus === AWAITING_FINAL_REGISTRATION;
  const finalNotice =
    finalReg.result === 'DONE' || finalReg.result === 'ALREADY_DONE'
      ? entryStatus === 'CANCELLED'
        ? labels.finalCancelled
        : labels.finalConfirmed
      : finalReg.result === 'QUOTA_FULL'
        ? labels.finalQuotaFull
        : null;

  const date = formatDateBlock(item.startDate, locale);
  const deadline = item.lastEntryDate ? formatFull(item.lastEntryDate, locale) : '';
  const detailHref = typeof item.url === 'string' && item.url.trim() ? item.url.trim() : undefined;
  const durationH = formatDuration(item.duration, locale);
  const eduText = item.educationLevels?.map((e) => EDU_LABELS[e] ?? e).join(', ');
  const slotsOpen = !item.entriesClosed && !item.quotaFull;
  const alreadyRegistered = Boolean(item.registered) || applied;
  const status = statusOf({ ...item, registered: alreadyRegistered }, labels, urgentDays);
  const isRow = variant === 'row';
  // Mirrors the `eligible === false` arm of statusOf/the action row: the chip
  // only reads "Şartları Karşılamıyorsunuz" once the closed/registered checks
  // have passed, so that is exactly when the reasons are worth offering.
  const ineligible = item.eligible === false && slotsOpen && !alreadyRegistered;
  const reasons = item.ineligibleReasons ?? (item.ineligibleReason ? [item.ineligibleReason] : []);

  const poster = (
    <div style={isRow ? rowPosterStyle : posterStyle} className="akd-btk-poster">
      {item.thumbnail ? (
        <img src={item.thumbnail} alt="" style={posterImgStyle} loading="lazy" decoding="async" />
      ) : (
        <div style={posterFallbackStyle} />
      )}
      <div style={posterScrimStyle} />

      {date ? (
        <div style={dateBadgeStyle}>
          <span style={dateDayStyle}>{date.day}</span>
          <span style={dateMonthStyle}>{date.month}</span>
          <span style={dateYearStyle}>{date.year}</span>
        </div>
      ) : null}

      {item.eventType || item.category ? (
        <div style={badgeStackStyle}>
          {item.eventType ? (
            <EventTypeBadge name={item.eventType} chip={typeChip} label={labels.filterByType} />
          ) : null}
          {item.category ? (
            <span style={{ ...categoryBadgeStyle, ...categoryTone(item.category) }}>
              {item.category}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const statusChip = (
    <span style={{ ...statusChipStyle, ...STATUS_TONES[status.tone] }}>
      {status.tone === 'urgent' ? <IconClock /> : null}
      {status.text}
      {ineligible ? (
        <IneligibleInfo
          reasons={reasons}
          title={labels.ineligibleWhy}
          triggerLabel={labels.ineligibleWhyHint}
        />
      ) : null}
    </span>
  );

  const meta = (
    <div style={metaRowStyle}>
      {shortVenue(item) ? (
        <span style={metaItemStyle}>
          <IconMapPin />
          {shortVenue(item)}
        </span>
      ) : null}
      {item.eventType ? (
        <span style={metaItemStyle}>
          <IconUsers />
          {item.eventType}
        </span>
      ) : null}
      {durationH ? (
        <span style={metaItemStyle}>
          <IconClock />
          {durationH} {labels.hour}
        </span>
      ) : null}
      {eduText ? (
        <span style={metaItemStyle}>
          <IconGraduation />
          {eduText}
        </span>
      ) : null}
    </div>
  );

  const deadlineRow = deadline ? (
    <div style={deadlineRowStyle}>
      <IconCalendar />
      {labels.lastEntry} {deadline}
    </div>
  ) : null;

  const actions = (
    <div className="akd-btk-actions" style={isRow ? rowActionsStyle : actionsStyle}>
      <Button
        {...(detailHref ? { href: detailHref, target: '_blank', rel: 'noopener noreferrer' } : {})}
        variant="outline"
        size="lg"
        className="akd-btk-action-btn"
        style={isRow ? rowDetailBtnStyle : detailBtnStyle}
      >
        <IconInfo />
        {labels.detail}
      </Button>
      {awaitingFinal ? (
        // Invited to kesin kayıt: the decision replaces the inert "Onay Bekliyor"
        // chip, because right now the participant IS the next step.
        <>
          <Button
            variant="brand"
            size="lg"
            className="akd-btk-action-btn"
            disabled={finalReg.busy}
            style={isRow ? rowApplyBtnStyle : applyBtnStyle}
            onClick={() => void finalReg.confirm()}
          >
            {labels.confirmFinal}
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="akd-btk-action-btn"
            disabled={finalReg.busy}
            style={isRow ? rowApplyBtnStyle : applyBtnStyle}
            onClick={() => void finalReg.cancel()}
          >
            {labels.cancelFinal}
          </Button>
        </>
      ) : alreadyRegistered ? (
        <Button
          disabled
          variant="secondary"
          size="lg"
          className="akd-btk-action-btn"
          style={isRow ? rowApplyBtnStyle : applyBtnStyle}
        >
          {finalNotice ?? labels.pending}
        </Button>
      ) : !slotsOpen ? null : item.eligible === false ? (
        // Listed but not applicable (9476 SHOW_BLOCKED): the reason rides on
        // the control itself, so the card explains rather than just refusing.
        <Button
          disabled
          variant="secondary"
          size="lg"
          className="akd-btk-action-btn"
          title={reasons.join(' · ') || labels.ineligible}
          style={{ ...(isRow ? rowApplyBtnStyle : applyBtnStyle), ...ineligibleBtnStyle }}
        >
          {labels.ineligible}
        </Button>
      ) : isSignedIn ? (
        <Button
          variant="brand"
          size="lg"
          className="akd-btk-action-btn"
          style={isRow ? rowApplyBtnStyle : applyBtnStyle}
          onClick={() => setApplyOpen(true)}
        >
          {labels.apply}
        </Button>
      ) : (
        <Button
          href="/sign-in"
          variant="brand"
          size="lg"
          className="akd-btk-action-btn"
          style={isRow ? rowApplyBtnStyle : applyBtnStyle}
        >
          {labels.apply}
        </Button>
      )}
    </div>
  );

  const applyModal =
    isSignedIn && slotsOpen && !alreadyRegistered && item.eligible !== false ? (
      <ActivityApplicationModal
        open={applyOpen}
        onOpenChange={setApplyOpen}
        eventId={item.id}
        applicationFormId={item.applicationFormId}
        eventTitle={item.title}
        eventSubtitle={fullVenue(item)}
        onSuccess={() => setApplied(true)}
      />
    ) : null;

  if (isRow) {
    return (
      <Card asChild>
        <article className="akd-btk-card akd-btk-row" style={{ ...cardReset, ...rowStyle }}>
          {poster}
          <div style={rowBodyStyle}>
            {statusChip}
            <h3 style={rowTitleStyle}>{item.title}</h3>
            {meta}
            {deadlineRow}
          </div>
          {actions}
          {applyModal}
        </article>
      </Card>
    );
  }

  return (
    <Card asChild>
      <article className="akd-btk-card" style={{ ...cardReset, ...cardStyle }}>
        {poster}
        <div style={cardBodyStyle}>
          {statusChip}
          <h3 style={cardTitleStyle}>{item.title}</h3>
          {meta}
          {deadlineRow}
          {actions}
        </div>
        {applyModal}
      </article>
    </Card>
  );
}

/** Event-type badge — a filter toggle when the type resolves to a taxonomy id
 *  (9493), plain text otherwise. */
function EventTypeBadge({
  name,
  chip,
  label,
}: {
  name: string;
  chip?: TypeChipAction;
  label: string;
}): ReactNode {
  if (!chip) return <span style={eventTypeBadgeStyle}>{name}</span>;
  return (
    <button
      type="button"
      onClick={chip.onToggle}
      aria-pressed={chip.active}
      title={label}
      className="akd-btk-type-chip"
      style={chip.active ? eventTypeBadgeActiveStyle : eventTypeBadgeButtonStyle}
    >
      {name}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDateBlock(
  ms: number | undefined,
  locale: string,
): { day: string; month: string; year: string } | null {
  if (!ms) return null;
  try {
    const d = new Date(ms);
    return {
      day: d.toLocaleDateString(locale || 'tr', { day: '2-digit' }),
      month: d.toLocaleDateString(locale || 'tr', { month: 'short' }).toUpperCase(),
      year: String(d.getFullYear()),
    };
  } catch {
    return null;
  }
}

function formatFull(ms: number, locale: string): string {
  try {
    return new Date(ms).toLocaleDateString(locale || 'tr', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/** Minutes → hours, one decimal when not whole ("2", "2,5"). */
function formatDuration(minutes: number | undefined, locale: string): string {
  if (typeof minutes !== 'number' || minutes <= 0) return '';
  const hours = minutes / 60;
  try {
    return hours.toLocaleString(locale || 'tr', { maximumFractionDigits: 1 });
  } catch {
    return String(Math.round(hours));
  }
}

/** Stable colour per category name so the same field always reads the
 *  same across cards without the backend supplying a colour. */
function categoryTone(name: string): CSSProperties {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return CATEGORY_TONES[hash % CATEGORY_TONES.length]!;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  style: { flexShrink: 0 } as CSSProperties,
};

function IconCalendar(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function IconMapPin(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function IconClock(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}
function IconUsers(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconGraduation(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  );
}
function IconInfo(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}
function IconTicket(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8Z" />
      <path d="M13 6v12" />
    </svg>
  );
}
function IconGrid(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="7" cy="7" r="3" />
      <circle cx="17" cy="7" r="3" />
      <circle cx="7" cy="17" r="3" />
      <circle cx="17" cy="17" r="3" />
    </svg>
  );
}
function IconRefresh(): ReactNode {
  return (
    <svg {...ICON_PROPS} width={19} height={19}>
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

/** Inline spinner for the result-count and load-more slots while a fetch is
 *  in flight — a lucide icon rather than a text placeholder. */
function Loading(): ReactNode {
  return <Loader2Icon aria-hidden width={14} height={14} className="akd-btk-spin" />;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const BTK_CSS = `
.akd-btk-card{transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease}
.akd-btk-card:hover{transform:translateY(-4px);border-color:var(--color-brand-500,#22d3ee);box-shadow:var(--shadow-lg,0 12px 32px rgba(0,0,0,.18))}
.akd-btk-card:hover img{transform:scale(1.05)}
/* A row spans the page — lifting it reads as the whole list jumping, so it
   keeps the border/shadow feedback without the translate. */
.akd-btk-row:hover{transform:none}
.akd-btk-type-chip{transition:background .15s ease,color .15s ease}
.akd-btk-type-chip:hover{background:var(--color-brand-500,#22d3ee);color:var(--color-text-on-brand,#06272e)}
.akd-btk-pill{transition:background .15s ease,border-color .15s ease,color .15s ease}
.akd-btk-pill:hover{border-color:var(--color-brand-500,#22d3ee)}
.akd-btk-clear{transition:border-color .15s ease,color .15s ease}
.akd-btk-clear:hover{border-color:var(--color-brand-500,#22d3ee);color:var(--color-brand-500,#22d3ee)}
.akd-btk-pills{scrollbar-width:none}
.akd-btk-pills::-webkit-scrollbar{display:none}
.akd-btk-spin{animation:akd-btk-spin .8s linear infinite;vertical-align:-0.15em}
@keyframes akd-btk-spin{to{transform:rotate(360deg)}}

@media(max-width:1400px){
  .akd-btk-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important}
}
@media(max-width:900px){
  .akd-btk-row{flex-direction:column!important}
  .akd-btk-row .akd-btk-poster{width:100%!important;aspect-ratio:var(--app-activity-card-image-aspect-ratio, 370 / 206)!important}
  .akd-btk-row .akd-btk-actions{width:100%!important;flex-direction:row!important;padding:0 20px 16px!important}
}
@media(max-width:1024px){
  .akd-btk-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}
  .akd-btk-filters{grid-template-columns:repeat(2,minmax(0,1fr))!important}
}
@media(max-width:640px){
  .akd-btk-grid{grid-template-columns:minmax(0,1fr)!important}
  .akd-btk-filters{grid-template-columns:minmax(0,1fr)!important}
  .akd-btk-card:hover{transform:none!important}
}
@media(max-width:800px){
    .akd-btk-actions{flex-direction:column!important}
   .akd-btk-action-btn{width:100%!important; padding-block:7px!important; }
}
@media(max-width:480px){
  .akd-btk-sort-wrap{flex-wrap:wrap;width:100%}
  .akd-btk-search{flex-basis:100%!important}
  .akd-btk-sort-select{flex:1 1 auto!important;width:100%!important}
}
`;

const headingStyle: CSSProperties = {
  margin: '0 0 20px',
  fontSize: 'var(--font-size-2xl, 24px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
};

const filterBarStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 12,
  marginBottom: 18,
};

/** The filter field box — applied to the SelectField trigger itself, so the
 *  entire box (icon, caption and value) is one click target. */
const filterFieldStyle: CSSProperties = {
  gap: 10,
  height: 56,
  padding: '0 14px',
  borderRadius: 'var(--radius-lg, 12px)',
  fontSize: 'var(--font-size-sm, 13px)',
  fontWeight: 500,
};

const pillRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 14,
};

const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 34,
  padding: '0 16px',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle, #e5e7eb)',
  borderRadius: 999,
  background: 'transparent',
  color: 'var(--color-text-secondary, #6b7280)',
  fontFamily: 'inherit',
  fontSize: 'var(--font-size-sm, 13px)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const pillActiveStyle: CSSProperties = {
  ...pillStyle,
  background: 'var(--color-brand-500, #22d3ee)',
  borderColor: 'var(--color-brand-500, #22d3ee)',
  color: 'var(--color-text-on-brand, #06272e)',
  fontWeight: 600,
};

const resultRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  margin: '0 0 16px',
};

const countStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const resultLeftStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

const sortWrapStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

const sortLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const sortSelectStyle: CSSProperties = {
  height: 34,
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: 'var(--font-size-sm, 13px)',
};

const searchInputStyle: CSSProperties = {
  height: 34,
  padding: '0 12px',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-primary, #111827)',
  background: 'var(--color-surface-card, #fff)',
  outline: 'none',
  flex: '1 1 180px',
  minWidth: 0,
  boxSizing: 'border-box',
};

const skeletonStyle: CSSProperties = {
  minHeight: 380,
  borderRadius: 'var(--radius-xl, 16px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
  opacity: 0.6,
};

// `cardStyle` / `rowStyle` (the `.akd-btk-card` `<article>`) are this block's
// own card surface, spread after the shared `cardReset` onto `<Card asChild>`.
const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle, #e5e7eb)',
  borderRadius: 'var(--radius-xl, 16px)',
  background: 'var(--color-surface-card, #fff)',
  overflow: 'hidden',
};

// --- list layout (9446) -----------------------------------------------------

/** One event per full-width row, all of them on the same page. */
const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

const rowStyle: CSSProperties = {
  ...cardStyle,
  flexDirection: 'row',
  alignItems: 'stretch',
};

/**
 * Fixed-size poster (~370×206) so every row's image reads the same instead
 * of stretching to match however tall that row's text happens to be.
 * {@code alignSelf: 'center'} is what keeps it fixed — the row's own
 * {@code alignItems: 'stretch'} would otherwise force the image to grow.
 */
const rowPosterStyle: CSSProperties = {
  position: 'relative',
  width: 370,
  aspectRatio: 'var(--app-activity-card-image-aspect-ratio, 370 / 206)',
  flexShrink: 0,
  alignSelf: 'center',
  overflow: 'hidden',
};

const rowBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '16px 20px',
  flex: 1,
  minWidth: 0,
};

/** Title wraps freely here — a row has the width for it, and unlike the grid
 *  there are no side-by-side neighbours to keep on a shared baseline. */
const rowTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-lg, 18px)',
  fontWeight: 700,
  lineHeight: 1.35,
  color: 'var(--color-text-primary, #111827)',
};

const rowActionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 10,
  width: 200,
  flexShrink: 0,
  padding: '16px 20px 16px 0',
};

/** The grid buttons grow to fill the card's action row; in a row the actions
 *  column is as tall as the poster, so they keep their natural height. */
const rowDetailBtnStyle: CSSProperties = {
  borderRadius: 'var(--radius-md, 8px)',
};

const rowApplyBtnStyle: CSSProperties = {
  borderRadius: 'var(--radius-md, 8px)',
  fontWeight: 600,
};

const rowSkeletonStyle: CSSProperties = {
  minHeight: 168,
  borderRadius: 'var(--radius-xl, 16px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
  opacity: 0.6,
};

const posterStyle: CSSProperties = {
  position: 'relative',
  aspectRatio: 'var(--app-activity-card-image-aspect-ratio, 16 / 9)',
  overflow: 'hidden',
  flexShrink: 0,
};

const posterImgStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'transform .25s ease',
};

const posterFallbackStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  background:
    'linear-gradient(135deg, var(--color-brand-600, #0e7490), var(--color-brand-400, #67e8f9))',
};

const posterScrimStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to bottom, rgba(0,0,0,.35) 0%, transparent 45%)',
  pointerEvents: 'none',
};

const dateBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  left: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  minWidth: 54,
  padding: '8px 10px',
  borderRadius: 'var(--radius-md, 10px)',
  background: 'rgba(9, 12, 20, .78)',
  backdropFilter: 'blur(4px)',
  color: '#fff',
  lineHeight: 1,
};

const dateDayStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl, 20px)',
  fontWeight: 700,
};

const dateMonthStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
};

const dateYearStyle: CSSProperties = {
  fontSize: 12,
  opacity: 0.7,
};

/** Top-right badge column on the poster: event type over category. */
const badgeStackStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 6,
  maxWidth: '60%',
};

/** Event type ("Yüz Yüze", "Çevrim İçi") — glassy, so it reads as chrome
 *  next to the colour-coded category badge below it. */
const eventTypeBadgeStyle: CSSProperties = {
  maxWidth: '100%',
  padding: '5px 10px',
  borderRadius: 'var(--radius-sm, 6px)',
  background: 'rgba(9, 12, 20, .78)',
  backdropFilter: 'blur(4px)',
  color: '#fff',
  fontSize: 'var(--font-size-xs, 11px)',
  fontWeight: 600,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Same badge as a filter toggle (9493) — reads identically until hovered. */
const eventTypeBadgeButtonStyle: CSSProperties = {
  ...eventTypeBadgeStyle,
  display: 'block',
  border: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const eventTypeBadgeActiveStyle: CSSProperties = {
  ...eventTypeBadgeButtonStyle,
  background: 'var(--color-brand-500, #22d3ee)',
  color: 'var(--color-text-on-brand, #06272e)',
};

const categoryBadgeStyle: CSSProperties = {
  maxWidth: '100%',
  padding: '5px 10px',
  borderRadius: 'var(--radius-sm, 6px)',
  fontSize: 'var(--font-size-xs, 11px)',
  fontWeight: 600,
  color: '#fff',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Deterministic per-category accents (theme tokens first, literal
 *  fallbacks for tenants that don't define the extended palette). */
const CATEGORY_TONES: CSSProperties[] = [
  { background: 'var(--color-violet-500, #7c3aed)' },
  { background: 'var(--color-orange-500, #ea580c)' },
  { background: 'var(--color-info-500, #2563eb)' },
  { background: 'var(--color-success-500, #059669)' },
  { background: 'var(--color-cyan-500, #0891b2)' },
  { background: 'var(--color-pink-500, #db2777)' },
];

const cardBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 16px 16px',
  flex: 1,
};

const statusChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: 5,
  padding: '4px 10px',
  borderRadius: 'var(--radius-sm, 6px)',
  fontSize: 'var(--font-size-xs, 11px)',
  fontWeight: 600,
};

const STATUS_TONES: Record<StatusTone, CSSProperties> = {
  open: {
    background: 'color-mix(in srgb, var(--color-success-500, #10b981) 18%, transparent)',
    color: 'var(--color-success-600, #10b981)',
  },
  urgent: {
    background: 'color-mix(in srgb, var(--color-amber-500, #f59e0b) 20%, transparent)',
    color: 'var(--color-amber-600, #f59e0b)',
  },
  closed: {
    background: 'color-mix(in srgb, var(--color-danger-500, #ef4444) 16%, transparent)',
    color: 'var(--color-danger-600, #ef4444)',
  },
  registered: {
    background: 'color-mix(in srgb, var(--color-info-500, #3b82f6) 18%, transparent)',
    color: 'var(--color-info-600, #3b82f6)',
  },
};

/** Always exactly two lines tall (clamped at two, reserved at two) so the
 *  meta row and the action buttons sit on the same baseline across a row of
 *  cards no matter how long each title wraps at the current breakpoint. */
const cardTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-lg, 17px)',
  fontWeight: 700,
  lineHeight: 1.35,
  minHeight: 'calc(1.35em * 2)',
  maxHeight: 'calc(1.35em * 2)',
  color: 'var(--color-text-primary, #111827)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};

const metaRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 16,
  rowGap: 6,
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const metaItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};

const deadlineRowStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  paddingTop: 2,
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 10,
  marginTop: 'auto',
  paddingTop: 12,
};

const detailBtnStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  borderRadius: 'var(--radius-md, 8px)',
};

const applyBtnStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  borderRadius: 'var(--radius-md, 8px)',
  fontWeight: 600,
};

/** The 9476 label is a full sentence ("Şartları Karşılamıyorsunuz"), not the
 *  verb phrase every other action carries, and the shared Button is
 *  `whitespace-nowrap` + `shrink-0` — so at the action column's fixed 200px it
 *  used to spill out past the card edge. Let it wrap to two compact lines
 *  inside the column instead; `stretch` keeps it level with "Detayları Gör". */
const ineligibleBtnStyle: CSSProperties = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  textAlign: 'center',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  height: 'auto',
  minHeight: 40,
  paddingBlock: 4,
  fontSize: 'var(--font-size-xs, 12px)',
  lineHeight: 1.3,
};

const messageStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #6b7280)',
  padding: 'var(--space-8, 32px) 0',
  textAlign: 'center',
};

const showMoreWrapStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'var(--space-6, 24px)',
};
